import { HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import { NgModule, TransferState } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouteReuseStrategy } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { AndroidPermissions } from '@awesome-cordova-plugins/android-permissions/ngx';
import { AppAvailability } from '@awesome-cordova-plugins/app-availability/ngx';

import { LocationAccuracy } from '@awesome-cordova-plugins/location-accuracy/ngx';
import {
  MsalBroadcastService,
  MsalGuard,
  MsalInterceptor,
  MsalModule,
  MsalRedirectComponent,
  MsalService,
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  InteractionType,
  PublicClientApplication,
} from '@azure/msal-browser';
import { Box } from '@core/providers/box';
import { environment } from '@env';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ImgConstants } from './core/constants/img-constants';
import { StorageConstants } from './core/constants/storage-constants';
import { StringConstants } from './core/constants/string-constants';
import { WebAppInfo } from './core/constants/web-versions';
import { CoreModule } from './core/core.module';
import { Dropbox } from './core/providers/dropbox';
import { Onedrive } from './core/providers/onedrive';
import { SharedModule } from './shared/shared.module';
import { Capacitor } from '@capacitor/core';
import { InAppBrowser } from '@awesome-cordova-plugins/in-app-browser/ngx';

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/languages/', '.json');
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    IonicModule.forRoot({ innerHTMLTemplatesEnabled: true }),
    AppRoutingModule,
    SharedModule,
    PdfViewerModule,
    CoreModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
    MsalModule.forRoot(
      new PublicClientApplication({
        // MSAL Configuration
        auth: {
          clientId: environment.msalClientId, // new
          authority: 'https://login.microsoftonline.com/common',
          redirectUri: environment.redirectBaseUrl + 'onedrive',
        },
        cache: {
          cacheLocation: BrowserCacheLocation.LocalStorage,
          storeAuthStateInCookie: true, // set to true for IE 11
        },
        system: {
          loggerOptions: {
            loggerCallback: () => {},
            piiLoggingEnabled: false,
          },
        },
      }),
      {
        interactionType: InteractionType.Redirect, // MSAL Guard Configuration
        authRequest: {
          scopes: [
            'user.read',
            'Files.Read.All',
            'offline_access',
            'Mail.Read',
          ],
        },
      },
      {
        interactionType: InteractionType.Redirect, // MSAL Interceptor Configuration
        protectedResourceMap: new Map([
          [
            'https://graph.microsoft.com/v1.0/me',
            ['user.read', 'Files.Read.All', 'Mail.Read'],
          ],
        ]),
      }
    ),
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !Capacitor.isNativePlatform(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
  providers: [
    InAppBrowser,
    ImgConstants,
    StringConstants,
    StorageConstants,
    WebAppInfo,
    Dropbox,
    Onedrive,
    Box,
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    AppAvailability,
    LocationAccuracy,
    TransferState,
    AndroidPermissions,
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
})
export class AppModule {}
